import { useEffect, useRef, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Database, Save, Play, Square } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

const DEFAULT_JSON_URL =
  "https://simsws.skoda-auto.de/webservice/partner/1.1/partner_data.php?f=json&k=b3d3VUw4Q0o2SlFiNE0vTmgvalMrK1l1eVViNlJIamJrRUxKYjNwOVJVQk5SSDVYUE90UnZPa24yc2dSOGdOVQ==";
const DEFAULT_XML_URL = "https://simsws.skoda-auto.de/webservice/K2/2.2/k2DealerData.php";

const POLL_INTERVAL_MS = 3000;

function CounterGrid({ counters }) {
  const entries = Object.entries(counters || {});
  if (entries.length === 0) return null;
  return (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4" data-testid="dealer-import-counters">
      {entries.map(([key, value]) => (
        <div key={key} className="rounded-md border border-border bg-secondary/40 px-3 py-2">
          <p className="data-label text-[10px] text-muted-foreground">{key.replace(/_/g, " ")}</p>
          <p className="text-lg font-semibold tabular-nums">{value}</p>
        </div>
      ))}
    </div>
  );
}

function PhotoProgress({ progress, label = "Employee photos", noun = "photos", testId = "dealer-import-photo-progress" }) {
  if (!progress || !progress.total) return null;
  const pct = Math.min(100, Math.round((progress.done / progress.total) * 100));
  return (
    <div className="mt-4" data-testid={testId}>
      <p className="data-label mb-1 flex items-center justify-between text-[10px]">
        <span>{label}</span>
        <span className="tabular-nums">{progress.done} / {progress.total}</span>
      </p>
      <div className="h-2 w-full overflow-hidden rounded-full bg-secondary/60">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-1 text-[10px] text-muted-foreground">
        {progress[`${noun}_downloaded`] ?? 0} downloaded · {progress[`${noun}_skipped_existing`] ?? 0} already local ·{" "}
        {progress[`${noun}_failed`] ?? 0} failed
      </p>
    </div>
  );
}

function PhotoErrors({ errors, label = "Photo", testId = "dealer-import-photo-errors" }) {
  if (!errors) return null;
  if (errors.storage_error) {
    return (
      <div className="mt-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-xs" data-testid={testId}>
        <p className="data-label mb-1 text-destructive">{label} storage not writable — {label.toLowerCase()} phase skipped</p>
        <p className="break-all font-mono">{errors.storage_error}</p>
      </div>
    );
  }
  const samples = errors.samples || [];
  return (
    <div className="mt-4" data-testid={testId}>
      <p className="data-label mb-2">
        {label} failures ({errors.total}) —{" "}
        {Object.entries(errors.by_stage || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
      </p>
      <div className="mb-2 rounded-md border border-border">
        <table className="w-full text-xs">
          <tbody>
            {(errors.top_errors || []).map((e, i) => (
              <tr key={i} className="border-t border-border first:border-t-0">
                <td className="px-2 py-1 align-top font-mono">{e.error}</td>
                <td className="whitespace-nowrap px-2 py-1 text-right align-top tabular-nums">{e.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">First {samples.length} failures</summary>
        <div className="mt-1 max-h-56 overflow-y-auto rounded-md border border-border">
          <table className="w-full">
            <tbody>
              {samples.map((s, i) => (
                <tr key={i} className="border-t border-border first:border-t-0">
                  <td className="whitespace-nowrap px-2 py-1 align-top font-mono text-muted-foreground">{s.stage}</td>
                  <td className="px-2 py-1 align-top break-all font-mono">{s.url || s.employee_id}</td>
                  <td className="px-2 py-1 align-top">{s.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function WarningsList({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  const shown = warnings.slice(0, 100);
  return (
    <div className="mt-4">
      <p className="data-label mb-2">Warnings ({warnings.length})</p>
      <div className="max-h-56 overflow-y-auto rounded-md border border-border" data-testid="dealer-import-warnings">
        <table className="w-full text-xs">
          <tbody>
            {shown.map((w, i) => (
              <tr key={i} className="border-t border-border first:border-t-0">
                <td className="whitespace-nowrap px-2 py-1 align-top font-mono text-muted-foreground">{w.code}</td>
                <td className="whitespace-nowrap px-2 py-1 align-top font-mono">{w.dealer_code}</td>
                <td className="px-2 py-1 align-top">{w.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {warnings.length > shown.length && (
        <p className="mt-1 text-xs text-muted-foreground">and {warnings.length - shown.length} more…</p>
      )}
    </div>
  );
}

export function DealerFeedImportCard() {
  const { user } = useAuth();
  const isSystemAdmin = hasGlobalPermission(user, PERM.IMPORTS_RUN);

  const [jsonUrl, setJsonUrl] = useState(DEFAULT_JSON_URL);
  const [xmlUrl, setXmlUrl] = useState(DEFAULT_XML_URL);
  const [photoHost, setPhotoHost] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [importPhotos, setImportPhotos] = useState(false);
  const [importLogos, setImportLogos] = useState(false);
  const [session, setSession] = useState(null);

  const intervalRef = useRef(null);
  const running = session?.status === "running";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/settings");
        const map = Object.fromEntries(data.map((s) => [s.key, s.value]));
        setJsonUrl(map.dealer_feed_json_url || DEFAULT_JSON_URL);
        setXmlUrl(map.dealer_feed_xml_url || DEFAULT_XML_URL);
        setPhotoHost(map.dealer_photo_host_suffix || "");
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      try {
        const { data } = await client.get("/admin/dealer-import/sessions", { params: { limit: 1 } });
        if (data?.[0]) setSession(data[0]);
      } catch {
        // No permission or nothing yet — the card just shows no recent run.
      }
    })();
  }, []);

  const pollSession = async (sessionId) => {
    try {
      const { data } = await client.get(`/admin/dealer-import/sessions/${sessionId}`);
      setSession(data);
    } catch {
      // Transient poll failure — keep last-known session state, try again next tick.
    }
  };

  useEffect(() => {
    clearInterval(intervalRef.current);
    if (running && session?.id) {
      intervalRef.current = setInterval(() => pollSession(session.id), POLL_INTERVAL_MS);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, session?.id]);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        client.put("/settings/dealer_feed_json_url", { value: jsonUrl.trim() }),
        client.put("/settings/dealer_feed_xml_url", { value: xmlUrl.trim() }),
        client.put("/settings/dealer_photo_host_suffix", { value: photoHost.trim().replace(/^\.+|\.+$/g, "") }),
      ]);
      toast.success("Dealer feed import settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runImport = async () => {
    setTriggering(true);
    try {
      const { data } = await client.post("/admin/dealer-import/run", { import_photos: importPhotos, import_logos: importLogos });
      setSession(data);
      toast.success("Dealer feed import started");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to start import");
    } finally {
      setTriggering(false);
    }
  };

  const stopImport = async () => {
    if (!session?.id) return;
    if (!window.confirm("Stop the running import? It will finish the dealer record it's currently on, then stop.")) {
      return;
    }
    setStopping(true);
    try {
      await client.post(`/admin/dealer-import/sessions/${session.id}/stop`);
      toast.success("Stop requested — finishing the current record, then stopping…");
      await pollSession(session.id);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to stop import");
    } finally {
      setStopping(false);
    }
  };

  let statusCaption = null;
  if (session) {
    if (session.status === "running") {
      statusCaption = `Running (phase: ${session.phase}) — started ${new Date(session.started_at).toLocaleString()}`;
    } else if (session.status === "completed") {
      statusCaption = `Completed ${new Date(session.finished_at).toLocaleString()}`;
    } else if (session.status === "error") {
      statusCaption = `Failed: ${session.error_message || "unknown error"}`;
    } else if (session.status === "stopped") {
      statusCaption = `Stopped by request ${new Date(session.finished_at).toLocaleString()} (phase: ${session.phase})`;
    }
  }

  const disabled = !isSystemAdmin || loading || saving;

  return (
    <Card className={`mb-6 p-6 ${!isSystemAdmin ? "opacity-60" : ""}`} data-testid="settings-dealer-import-card">
      <div className="mb-1 flex items-center gap-2">
        <Database className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Dealer Feed Import</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Imports dealers from the Skoda partner JSON feed (creates/updates dealers + branches, marks
        dealers absent from the feed as deleted), then enriches each matched dealer with per-service
        hours and contacts from the XML feed. Dealers flagged &quot;don&apos;t import&quot; on the
        Dealers list are never touched.
        {!isSystemAdmin && (
          <span className="ml-2 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
            System Administrator only
          </span>
        )}
      </p>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label className="data-label mb-1 block">JSON feed URL</Label>
          <Input
            data-testid="settings-dealer-import-json-url"
            value={jsonUrl}
            onChange={(e) => setJsonUrl(e.target.value)}
            disabled={disabled}
            className="font-mono text-xs"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="data-label mb-1 block">XML feed URL</Label>
          <Input
            data-testid="settings-dealer-import-xml-url"
            value={xmlUrl}
            onChange={(e) => setXmlUrl(e.target.value)}
            disabled={disabled}
            className="font-mono text-xs"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="data-label mb-1 block">Photo host</Label>
          <Input
            data-testid="settings-dealer-import-photo-host"
            value={photoHost}
            onChange={(e) => setPhotoHost(e.target.value)}
            disabled={disabled}
            placeholder="e.g. skoda-auto.de"
            className="font-mono text-xs"
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Domain the employee photos are downloaded from, as &lt;dealer host&gt;.&lt;photo host&gt;.
            Leave blank to use the Dealer Host Suffix.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="dealer-import-import-photos"
            data-testid="settings-dealer-import-photos-checkbox"
            checked={importPhotos}
            onCheckedChange={(v) => setImportPhotos(v === true)}
            disabled={disabled || running}
          />
          <Label htmlFor="dealer-import-import-photos" className="cursor-pointer text-sm font-normal">
            Import photos
          </Label>
          <span className="text-xs text-muted-foreground">
            — when unchecked, the employee-photo download phase is skipped entirely
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="dealer-import-import-logos"
            data-testid="settings-dealer-import-logos-checkbox"
            checked={importLogos}
            onCheckedChange={(v) => setImportLogos(v === true)}
            disabled={disabled || running}
          />
          <Label htmlFor="dealer-import-import-logos" className="cursor-pointer text-sm font-normal">
            Import logos
          </Label>
          <span className="text-xs text-muted-foreground">
            — when unchecked, the dealer-logo download phase is skipped entirely
          </span>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="settings-dealer-import-run"
            variant="outline"
            onClick={runImport}
            disabled={disabled || triggering || running}
            className="gap-1.5"
          >
            <Play className="h-3.5 w-3.5" />
            {running ? "Import running…" : "Run Import"}
          </Button>
          {running && (
            <Button
              data-testid="settings-dealer-import-stop"
              variant="destructive"
              onClick={stopImport}
              disabled={!isSystemAdmin || stopping || session?.stop_requested}
              className="gap-1.5"
            >
              <Square className="h-3.5 w-3.5" />
              {session?.stop_requested ? "Stopping…" : "Stop Import"}
            </Button>
          )}
        </div>
        <Button
          data-testid="settings-dealer-import-save"
          onClick={save}
          disabled={disabled}
          className="gap-1.5"
        >
          <Save className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>

      {statusCaption && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid="settings-dealer-import-status">
          {statusCaption}
        </p>
      )}
      {session?.status === "running" && session.phase === "photos" && (
        <PhotoProgress progress={session.photo_progress} />
      )}
      {session?.status === "running" && session.phase === "logos" && (
        <PhotoProgress progress={session.logo_progress} label="Dealer logos" noun="logos" testId="dealer-import-logo-progress" />
      )}
      {session && <CounterGrid counters={session.counters} />}
      {session && <PhotoErrors errors={session.photo_errors} />}
      {session && <PhotoErrors errors={session.logo_errors} label="Logo" testId="dealer-import-logo-errors" />}
      {session && <WarningsList warnings={session.warnings} />}
    </Card>
  );
}
